import React from "react"
import { formatTimestamp } from "@ciq/shared/utils/format"
import "./LicenseBanner.css"

/**
 * @typedef {Object} LicenseBannerProps
 * @property {number} currentCount - Current number of active users
 * @property {number|null} maxCount - Maximum allowed user licenses (null = no limit)
 * @property {string|null} lastSyncedAt - ISO timestamp of last HubSpot license sync
 */

/**
 * Displays license usage status with color-coded banner.
 * Returns null when maxCount is null (no license limit configured).
 *
 * @param {LicenseBannerProps} props - Component props
 * @returns {React.ReactElement|null} License banner or null if no limit
 */
export function LicenseBanner({ currentCount, maxCount, lastSyncedAt }) {
  if (!maxCount) return null

  const count = Number(currentCount ?? 0)
  const max = Number(maxCount)

  let statusClass
  if (count > max) {
    statusClass = "license-banner--red"
  } else if (count === max) {
    statusClass = "license-banner--yellow"
  } else {
    statusClass = "license-banner--green"
  }

  const syncedDisplay = lastSyncedAt != null ? formatTimestamp(lastSyncedAt) : "Never"
  const overLimit = count > max
  const atLimit = count === max

  let icon = <span className="license-banner__icon license-banner__icon--circle">{"\u2713"}</span>
  let warningText = null
  if (overLimit) {
    icon = <span className="license-banner__icon license-banner__icon--circle">!</span>
    warningText = "This customer is over their license limit. Please remove users or upgrade their plan."
  } else if (atLimit) {
    icon = <span className="license-banner__icon">{"\u26A0"}</span>
    warningText = "This institution has reached their license limit."
  }

  return (
    <div className={`license-banner ${statusClass}`} data-testid="license-banner">
      <div className="license-banner__content">
        <span className="license-banner__usage">
          {icon}
          <strong>{count} of {max} licenses used</strong>  ({Math.round((count / max) * 100)}%)
        </span>
        {warningText && (
          <span className="license-banner__warning">{warningText}</span>
        )}
      </div>
      <span className="license-banner__synced">Last synced: {syncedDisplay}</span>
    </div>
  )
}
